import { type Address, CapacityLimitReason } from '@morpho-org/blue-sdk';
import { parseApiAsset, useReadOnlyVault, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useVaultV2DisabledLabel } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/hooks/useVaultV2DisabledLabel';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import { safeSentryBreadcrumb } from '@/helpers/sentry';
import useAssetCanTransferOnchain from '@/hooks/operation/useAssetCanTransferOnchain';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import useVaultV2TooltipMessage from '@/hooks/operation/vault/useVaultV2TooltipMessage';
import type { VaultV1ActionPanelData } from '../VaultSimpleActionPanel/vaultV1ActionPanelData';
import { useVaultV1 } from './useVaultV1';
import { useVaultV1Position } from './useVaultV1Position';
import { computeWithdrawFinalPosition } from './vaultV1Withdraw.helpers';
type VaultV1ByAddress = Pick<VaultV1ActionPanelData, 'asset'>;
interface UseVaultV1WithdrawLogicParams {
  vaultData: VaultV1ByAddress | null | undefined;
  vaultAddress: Address;
  /** Seeds the input, for a review that re-runs this logic with the panel's amount and MAX choice. */
  initialInput?: {
    amount: bigint;
    isMax: boolean;
  };
}
export function useVaultV1WithdrawLogic({
  vaultData: vaultDataParam,
  vaultAddress,
  initialInput
}: UseVaultV1WithdrawLogicParams) {
  const {
    account
  } = useWeb3Context();
  const vaultAsset = useMemo(() => getParsedValue(vaultDataParam?.asset, parseApiAsset), [vaultDataParam?.asset]);
  const chainId = vaultDataParam?.asset.chain.id;

  // Permissioned assets (Backed/Coinbase): block withdraw when the recipient
  // wallet isn't authorized to receive the vault asset, mirroring the
  // pre-migration guard.
  const canTransfer = useAssetCanTransferOnchain(account.address, vaultAsset?.address, chainId);

  // Issuer gate on the bundle itself: permissioned vault assets (Backed/Coinbase) must
  // whitelist VaultBundlesV1 — the contract withdrawals are routed through — or the tx reverts.
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain('bundles.vaultBundlesV1', vaultAsset?.address, vaultDataParam?.asset.chain.id);
  const {
    vault,
    vaultData,
    isLoading: isVaultLoading,
    error: vaultDataError
  } = useVaultV1(vaultAddress);
  const {
    userVaultShares,
    refetch: refetchPosition,
    isLoading: isLoadingPosition,
    error: userVaultPositionError
  } = useVaultV1Position({
    vaultAddress,
    chainId
  });
  const userVaultAssets = useMemo(() => isDefined(userVaultShares) ? vaultData?.toAssets(userVaultShares) : undefined, [vaultData, userVaultShares]);

  // `maxWithdraw` takes shares; its limiter tells us whether the user is capped
  // by their own position (`balance`) or by available vault liquidity.
  const maxWithdraw = useMemo(() => isDefined(userVaultShares) ? vaultData?.maxWithdraw(userVaultShares) : undefined, [vaultData, userVaultShares]);
  const [inputAmount, setInputAmount] = useState(initialInput?.amount ?? ZERO);
  const [isMax, setIsMax] = useState(initialInput?.isMax ?? false);

  // Withdrawing the full position: redeem all shares so no dust is left behind.
  const isClosePosition = isMax && maxWithdraw?.limiter === CapacityLimitReason.balance;
  const onMaxClick = useCallback((value: boolean) => {
    setIsMax(value);
  }, []);

  // While MAX is active, keep inputAmount synced to the live (accrued) ceiling so accrued
  // interest is withdrawn instead of left as dust: position and vault liquidity keep polling,
  // so a value captured at click time goes stale. `LargeAssetInput` clears `isMax` on manual
  // edits, so this never overrides a manual amount.
  useEffect(() => {
    if (isMax) setInputAmount(maxWithdraw?.value ?? 0n);
  }, [isMax, maxWithdraw?.value]);
  const finalPosition = useMemo(() => computeWithdrawFinalPosition({
    userVaultAssets,
    inputAmount,
    isClosePosition
  }), [userVaultAssets, inputAmount, isClosePosition]);
  const buildTransaction = useCallback(() => {
    if (!account.address || !isDefined(userVaultShares) || !isDefined(vaultData)) return undefined;
    if (userVaultShares <= ZERO) return undefined;
    if (isClosePosition) {
      return vault.redeem({
        shares: userVaultShares,
        userAddress: account.address
      });
    }
    if (inputAmount <= ZERO) return undefined;
    return vault.withdraw({
      amount: inputAmount,
      userAddress: account.address,
      vaultData
    });
  }, [account.address, userVaultShares, vaultData, vault, isClosePosition, inputAmount]);

  // The build is sync — only `getRequirements()` hits RPC, and the review dialog fetches those
  // via `useVaultWithdrawRequirements` once the user asks to review.
  const {
    transaction,
    buildError
  } = useMemo(() => {
    try {
      return {
        transaction: buildTransaction(),
        buildError: undefined
      };
    } catch (error) {
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Vault V1 withdraw handle build failed',
        level: 'warning',
        data: {
          chainId: vaultDataParam?.asset.chain.id,
          error: error instanceof Error ? error.message : String(error)
        }
      });
      return {
        transaction: undefined,
        buildError: error
      };
    }
  }, [buildTransaction, vaultDataParam?.asset.chain.id]);
  const localErrorMessage = useMemo(() => {
    if (isDefined(vaultDataError)) return 'Unable to get vault data';
    if (isDefined(buildError)) return 'Unable to prepare withdraw transaction';
    if (!isDefined(maxWithdraw)) return 'Unable to get vault withdraw amount';
    if (!isDefined(userVaultAssets) || isDefined(userVaultPositionError)) {
      return 'Unable to get user vault assets';
    }
    if (inputAmount <= maxWithdraw.value) return undefined;
    if (maxWithdraw.limiter === CapacityLimitReason.balance) return 'Insufficient Position amount';
    if (maxWithdraw.limiter === CapacityLimitReason.liquidity) return 'Insufficient Liquidity';
    return 'Maximum withdraw amount exceeded';
  }, [inputAmount, maxWithdraw, userVaultAssets, userVaultPositionError, vaultDataError, buildError]);
  const isLoading = isLoadingPosition || isVaultLoading;
  const tooltipMessage = useVaultV2TooltipMessage({
    address: vaultAddress,
    error: localErrorMessage,
    chainId
  });

  // Read-only vaults (deprecated/migrated) are display-only — keep them non-interactable
  // as the pre-migration flow did. The tooltip already surfaces the read-only reason.
  const {
    isReadOnly
  } = useReadOnlyVault({
    chainId,
    address: vaultAddress
  });
  const baseDisabledLabel = useVaultV2DisabledLabel({
    localErrorMessage,
    isInputEmpty: inputAmount === ZERO,
    isCorrectVaultV2: true
  });
  const disabledLabel = isReadOnly ? baseDisabledLabel ?? 'Unavailable' : baseDisabledLabel;
  const resetInput = useCallback(() => {
    setInputAmount(ZERO);
    setIsMax(false);
  }, []);
  return {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    resetInput,
    userVaultAssets,
    maxWithdraw,
    finalPosition,
    transaction,
    buildError,
    sdkAction: isClosePosition ? 'redeem' : 'withdraw',
    shareToken: vaultData,
    isLoading,
    localErrorMessage,
    disabledLabel,
    canTransfer,
    bundleCanTransfer,
    tooltipMessage,
    refetchPosition,
    error: {
      vaultDataError,
      userVaultPositionError
    }
  };
}